import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { botsApi } from '../../services/api';
import { CADENCE_TYPES, cadenceOfType, dayName, formatRun } from './cadence';

const EVERY = [1, 2, 3, 4, 6, 8, 12];
const field = 'h-8 rounded-lg border border-input bg-background px-2 text-[13px] text-foreground focus:outline-none focus:border-amber-500/60 dark:[color-scheme:dark]';

const DayChips = ({ value = [], onChange, lang, testid }) => (
  <div className="flex flex-wrap gap-1" data-testid={testid}>
    {[0, 1, 2, 3, 4, 5, 6].map((d) => {
      const on = value.includes(d);
      return (
        <button key={d} type="button" aria-pressed={on}
          onClick={() => {
            const next = on ? value.filter((x) => x !== d) : [...value, d].sort();
            if (next.length) onChange(next);
          }}
          className={`h-7 min-w-[38px] px-1.5 rounded-md border text-xs capitalize transition-colors ${on
            ? 'border-amber-500/50 bg-amber-500/15 text-amber-700 dark:text-amber-300'
            : 'border-input text-muted-foreground hover:text-foreground'}`}>
          {dayName(d, lang, 'short')}
        </button>
      );
    })}
  </div>
);

/** The next runs of a rhythm, checked by the server (the same rules the scheduler uses). */
export const CadencePreview = ({ cadence, timezone }) => {
  const { t, i18n } = useTranslation();
  const { data, error } = useQuery({
    queryKey: ['schedule-preview', cadence, timezone],
    queryFn: () => botsApi.schedulePreview(cadence, timezone).then((r) => r.data),
    staleTime: Infinity,
    retry: false,
  });
  if (error) {
    return <p className="text-xs text-red-500">{error?.response?.data?.detail || t('bots.cadence.invalid')}</p>;
  }
  if (!data) return null;
  return (
    <p className="text-xs text-muted-foreground" data-testid="cadence-preview">
      {data.next.length ? t('bots.cadence.next_runs', {
        when: data.next.map((n) => formatRun(n, i18n.language, timezone)).join(' · '),
      }) : t('bots.cadence.no_more_runs')}
    </p>
  );
};

/**
 * Picking a rhythm without cron strings: once, every few hours, daily,
 * weekdays, weekly on some days, monthly on a day. `value` is a cadence
 * as agents/schedules.py stores it.
 */
const CadenceEditor = ({ value, onChange, timezone, showPreview = true }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const c = value || cadenceOfType('weekdays');
  const set = (patch) => onChange({ ...c, ...patch });
  return (
    <div className="space-y-2.5" data-testid="cadence-editor">
      <div className="flex flex-wrap gap-1" role="radiogroup" aria-label={t('bots.cadence.label')}>
        {CADENCE_TYPES.map((type) => (
          <button key={type} type="button" role="radio" aria-checked={c.type === type}
            onClick={() => onChange(cadenceOfType(type, c))} data-testid={`cadence-${type}`}
            className={`h-7 px-2.5 rounded-full border text-xs transition-colors ${c.type === type
              ? 'border-amber-500/50 bg-amber-500/15 text-amber-700 dark:text-amber-300 font-medium'
              : 'border-input text-muted-foreground hover:text-foreground'}`}>
            {t(`bots.cadence.type_${type}`)}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
        {c.type === 'once' && (
          <input type="date" value={c.date || ''} onChange={(e) => set({ date: e.target.value })} className={field}
            aria-label={t('bots.cadence.date')} />
        )}
        {c.type === 'hourly' && (
          <>
            <span>{t('bots.cadence.every')}</span>
            <select value={c.every || 1} onChange={(e) => set({ every: Number(e.target.value) })} className={field}
              aria-label={t('bots.cadence.every')}>
              {EVERY.map((n) => <option key={n} value={n}>{new Intl.NumberFormat(lang, { style: 'unit', unit: 'hour', unitDisplay: 'long' }).format(n)}</option>)}
            </select>
            <span>{t('bots.cadence.from')}</span>
          </>
        )}
        {c.type === 'monthly' && (
          <select value={c.day || 1} onChange={(e) => set({ day: Number(e.target.value) })} className={field}
            aria-label={t('bots.cadence.day_of_month')}>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{t('bots.cadence.day_n', { day: d })}</option>)}
            <option value={-1}>{t('bots.cadence.last_day')}</option>
          </select>
        )}
        {c.type !== 'hourly' && <span>{t('bots.cadence.at')}</span>}
        <input type="time" value={c.time || '08:00'} onChange={(e) => set({ time: e.target.value })} className={field}
          aria-label={t('bots.cadence.time')} data-testid="cadence-time" />
        {c.type === 'hourly' && (
          <>
            <span>{t('bots.cadence.to')}</span>
            <input type="time" value={c.until || ''} onChange={(e) => set({ until: e.target.value || undefined })}
              className={field} aria-label={t('bots.cadence.until')} />
          </>
        )}
      </div>

      {(c.type === 'weekly' || c.type === 'hourly') && (
        <DayChips value={c.days || []} onChange={(days) => set({ days })} lang={lang} testid="cadence-days" />
      )}
      {showPreview && <CadencePreview cadence={c} timezone={timezone} />}
    </div>
  );
};

export default CadenceEditor;
